import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Wrench, ArrowLeftRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CR_ACTIONS = [
  { value: 'adjust_times', label: 'Adjust scheduled times', times: true },
  { value: 'late', label: 'Mark LATE', times: false },
  { value: 'no_show', label: 'Mark NO SHOW (missed)', times: false },
  { value: 'unable_to_attend', label: 'Unable to Attend (missed)', times: false },
  { value: 'early_finish', label: 'Early Finish', times: true },
  { value: 'extended', label: 'Extended Shift', times: true },
];

// Control Room actions on one shift: window-limited operational adjustment
// + staff replacement (any date). Server enforces all rules.
export default function CrShiftActions({ shift, onDone }) {
  const [adjOpen, setAdjOpen] = useState(false);
  const [repOpen, setRepOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState('adjust_times');
  const [start, setStart] = useState(shift.start_time || '');
  const [end, setEnd] = useState(shift.end_time || '');
  const [reason, setReason] = useState('');
  const [candidates, setCandidates] = useState(null);
  const [target, setTarget] = useState('');
  const needsTimes = CR_ACTIONS.find((a) => a.value === action)?.times;

  const submitAdjust = async () => {
    if (!reason.trim()) { toast.error('A reason/note is required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/control-room/shifts/${shift.id}/adjust`, {
        action, reason,
        start_time: needsTimes ? start : null,
        end_time: needsTimes ? end : null,
      });
      toast.success(r.data?.message || 'Saved');
      setAdjOpen(false); setReason('');
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Not allowed');
    } finally { setBusy(false); }
  };

  const openReplace = async () => {
    setRepOpen(true); setCandidates(null); setTarget(''); setReason('');
    try {
      const r = await axios.get(`${API}/admin/shifts/${shift.id}/reassign-candidates`);
      setCandidates(r.data || []);
    } catch { setCandidates([]); }
  };

  const submitReplace = async () => {
    if (!target) { toast.error('Pick a replacement'); return; }
    if (!reason.trim()) { toast.error('A reason/note is required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/shifts/${shift.id}/reassign`, {
        new_employee_id: target, notify: true, reason,
      });
      toast.success(`Replaced with ${r.data?.new_employee}`);
      setRepOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Replacement failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      <Button size="sm" variant="outline" onClick={() => setAdjOpen(true)}
        className="h-7 rounded-sm text-[11px] uppercase tracking-wider border-orange-500/40 text-orange-400 hover:bg-orange-500/10"
        data-testid={`cr-adjust-btn-${shift.id}`}>
        <Wrench className="w-3 h-3 mr-1" /> Adjust
      </Button>
      <Button size="sm" variant="outline" onClick={openReplace}
        className="h-7 rounded-sm text-[11px] uppercase tracking-wider border-border"
        data-testid={`cr-replace-btn-${shift.id}`}>
        <ArrowLeftRight className="w-3 h-3 mr-1" /> Replace Staff
      </Button>

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle>Operational Adjustment</DialogTitle>
            <DialogDescription>
              Allowed only for yesterday / today / tomorrow. Actual clock records are never changed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="rounded-sm h-9" data-testid="cr-action-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {CR_ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {needsTimes && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-sm h-9" data-testid="cr-start" /></div>
                <div><Label className="text-xs">End</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-sm h-9" data-testid="cr-end" /></div>
              </div>
            )}
            <div>
              <Label className="text-xs">Reason / note (required)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="rounded-sm" data-testid="cr-reason" />
            </div>
            <Button disabled={busy} onClick={submitAdjust} className="w-full rounded-sm bg-primary text-primary-foreground" data-testid="cr-adjust-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save adjustment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={repOpen} onOpenChange={setRepOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle>Replace Staff</DialogTitle>
            <DialogDescription>
              The replacement inherits this shift exactly (date, times, location, role). Busy colleagues are hidden.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="rounded-sm h-9" data-testid="cr-replace-select">
                <SelectValue placeholder={candidates === null ? 'Loading…' : `Select replacement (${(candidates || []).length} free)`} />
              </SelectTrigger>
              <SelectContent className="bg-card border-border max-h-56">
                {(candidates || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div>
              <Label className="text-xs">Reason / note (required)</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="rounded-sm" data-testid="cr-replace-reason" />
            </div>
            <Button disabled={busy} onClick={submitReplace} className="w-full rounded-sm bg-primary text-primary-foreground" data-testid="cr-replace-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm replacement'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
